/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { AddSharp } from "@mui/icons-material";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    LinkedOrder,
    TransferOrderArrayList,
} from "../../../@types/DRO/TransferOrderResponse";
import { TransferOrderApi } from "../../../api/TransferOrder/TransferOrderApi";
import { UseDebounce } from "../../../states/Hooks/UseDebounce";
import { useLoading } from "../../../views/Loader/LoadingContext";
import SideBarNavigation from "../../Layouts/Sidebar/Sidebar";
import TransferOrderTableComponent from "../../Layouts/Table/TransferOrderTableComponent";
import RoleGuard from "../../auth/RoleGuard";
import CustomDropdown from "../CustomDropdown/CustomDropdown";

const transferOrderStatusOptions = [
  { value: "", label: "ALL" },
  { value: "DRAFT", label: "Draft" },
  { value: "CREATED", label: "Created" },
  { value: "PICKED", label: "Picked" },
  { value: "IN_TRANSIT", label: "In Transit" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELED", label: "Cancelled" },
];

const getStatusBadge = (status: string) => {
  const statusMap: Record<string, { className: string; label: string }> = {
    DRAFT: { className: "bg-gray-100 text-gray-800", label: "Draft" },
    CREATED: { className: "bg-blue-100 text-blue-800", label: "Created" },
    COMPLETED: { className: "bg-green-100 text-green-800", label: "Completed" },
    PICKED: { className: "bg-red-100 text-red-800", label: "Picked" },
    IN_TRANSIT: {
      className: "bg-purple-100 text-purple-800",
      label: "In Transit",
    },
    CANCELED: { className: "bg-gray-100 text-gray-800", label: "Cancelled" },
  };
  return (
    statusMap[status] || {
      className: "bg-gray-100 text-gray-800",
      label: status,
    }
  );
};

const TransferOrderPage = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const columns = [
    "ID",
    "Reference",
    "From",
    "To",
    "Creation Date",
    "Status",
    "Created By",
    "Amount",
    "Category",
    "Linked TOs",
  ];
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(
    () => searchParams.get("status") || ""
  );
  const [typeFilter, setTypeFilter] = useState("");
  const debouncedSearch = UseDebounce(search, 500);

  // Update URL when status filter changes
  useEffect(() => {
    const currentStatus = searchParams.get("status") || "";
    if (currentStatus === statusFilter && searchParams.size === (statusFilter ? 1 : 0)) return;

    if (statusFilter) {
      setSearchParams({ status: statusFilter });
    } else {
      setSearchParams({});
    }
  }, [statusFilter, searchParams, setSearchParams]);
  const [totalPages, setTotalPages] = useState(1);
  const [data, setData] = useState<TransferOrderArrayList[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<{
    id: number;
    status: string;
  } | null>(null);
  const [newOrder, setNewOrder] = useState(false);
  const { setLoading } = useLoading();

  // Linked TOs state
  const [linkedOrders, setLinkedOrders] = useState<LinkedOrder[]>([]);
  const [isLinkedOrdersModalOpen, setIsLinkedOrdersModalOpen] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<number | null>(null);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter, typeFilter]);

  useEffect(() => {
    fetchTransferOrderData();
  }, [page, pageSize, debouncedSearch, statusFilter, typeFilter]);

  useEffect(() => {
    if (!selectedOrder) return;

    switch (selectedOrder.status) {
      case "DRAFT":
        return navigate(`/transfer-order/draft/${selectedOrder.id}${statusFilter ? `?status=${statusFilter}` : ''}`);
      case "CREATED":
        return navigate(`/transfer-order/${selectedOrder.id}${statusFilter ? `?status=${statusFilter}` : ''}`);
      case "PICKED":
        return navigate(`/transfer-order/picked/${selectedOrder.id}/${statusFilter ? `?status=${statusFilter}` : ''}`);
      case "IN_TRANSIT":
        return navigate(`/transfer-order/in-transit/${selectedOrder.id}/${statusFilter ? `?status=${statusFilter}` : ''}`);
      case "COMPLETED":
        return navigate(`/transfer-order/completed/${selectedOrder.id}/${statusFilter ? `?status=${statusFilter}` : ''}`);
      case "CANCELED":
        return navigate(`/transfer-order/cancelled/${selectedOrder.id}/${statusFilter ? `?status=${statusFilter}` : ''}`);
      default:
    }
  }, [selectedOrder]);

  const fetchTransferOrderData = async () => {
    setLoading(true);
    try {
      const response = await TransferOrderApi.api.getAllTransferOrderApi(
        page,
        pageSize,
        debouncedSearch,
        undefined,
        undefined,
        statusFilter,
        typeFilter
      );
      setData(response.data.data);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Error fetching transfer order data:", error);
    }
    setLoading(false);
  };

  const fetchLinkedOrders = async (orderId: number) => {
    setLoading(true);
    try {
      const response = await TransferOrderApi.api.getLinkedTransferOrderApi(
        orderId
      );
      setLinkedOrders(response.data.linkedOrders);
      setCurrentOrderId(orderId);
      setIsLinkedOrdersModalOpen(true);
    } catch (error) {
      console.error("Error fetching linked orders:", error);
    }
    setLoading(false);
  };

  const handleTransferOrder = () => {
    setNewOrder(true);
  };

  const handleBackToOrders = () => {
    setSelectedOrder(null);
    setNewOrder(false);
    fetchTransferOrderData();
  };

  const formatData = (data: TransferOrderArrayList[]) => {
    return data.map((item) => ({
      ID: item.id,
      Reference: item.referenceCode,
      From: item.sourceWarehouse?.name,
      To: item.destinationWarehouse?.name,
      "Creation Date": item.createdAt,
      Status: item.status,
      "Created By": item?.createdBy,
      Amount: item.amount, // <-- FIXED HERE
      Category: item.partCondition,
      "Linked TOs": item.id,
    }));
  };

  const renderStatusPage = () => {
    if (!selectedOrder) return null;

    const selectedOrderData = data.find((item) => item.id === selectedOrder.id);

    switch (selectedOrder.status) {
      case "DRAFT":
        return <Link to={`/transfer-order/draft/${selectedOrder.id}`}></Link>;
      case "CREATED":
        return <Link to={`/transfer-order/${selectedOrder.id}`}></Link>;
      case "COMPLETED":
        return (
          <Link to={`/transfer-order/completed/${selectedOrder.id}`}></Link>
        );
      case "PICKED":
        return <Link to={`/transfer-order/picked/${selectedOrder.id}`}></Link>;
      case "IN_TRANSIT":
        return (
          <Link to={`/transfer-order/in-transit/${selectedOrder.id}`}></Link>
        );
      default:
        return (
          <div className="p-6">
            <h2 className="text-2xl font-semibold mb-4">Order Details</h2>
            <p>No specific view for status: {selectedOrder.status}</p>
            <button
              onClick={handleBackToOrders}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded">
              Back to Orders
            </button>
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <div className="w-64 bg-white shadow-lg">
        <SideBarNavigation />
      </div>
      <div className="flex-1 p-6 ml-6 overflow-auto">
        <h1 className="text-3xl font-semibold mb-6 text-left">
          Transfer Orders
        </h1>

        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4"></div>
           <RoleGuard deny={["ROLE_FRANCHISE_INVENTORY_MANAGER"]}>
          <Link to={`/transfer-order/create${statusFilter ? `?status=${statusFilter}` : ""}`}>
            <Button
              variant="contained"
              startIcon={<AddSharp />}
              sx={{
                textTransform: "none",
                bgcolor: "#171F21",
                "&:hover": { bgcolor: "#0077F" },
                fontSize: "16px",
                borderRadius: "30px",
                px: 3,
                py: 1,
              }}
              onClick={handleTransferOrder}>
              Add Order
            </Button>
          </Link>
          </RoleGuard>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex items-center gap-4">
            <CustomDropdown
              label="Status:"
              options={transferOrderStatusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              minWidth="130px"
            />
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Type:</span>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option value="">ALL</option>
                <option value="WORKING">Working</option>
                <option value="NOT_WORKING">Not Working</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by reference code or warehouse"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-80 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="overflow-x-auto w-full">
            <TransferOrderTableComponent
              columns={columns}
              data={formatData(data)}
              setSelectedOrder={setSelectedOrder}
              currentPage={page}
              setCurrentPage={setPage}
              totalPages={totalPages}
              statusFilter={statusFilter}
              fetchLinkedOrders={fetchLinkedOrders}
            />
          </div>
        </div>

        {/* Linked Orders Modal */}
        <Dialog
          open={isLinkedOrdersModalOpen}
          onClose={() => setIsLinkedOrdersModalOpen(false)}
          maxWidth="md"
          fullWidth>
          <DialogTitle>
            Linked Transfer Orders{" "}
            {currentOrderId && `(For TO #${currentOrderId})`}
          </DialogTitle>
          <DialogContent>
            {linkedOrders.length > 0 ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linkedOrders.map((order) => {
                    const statusInfo = getStatusBadge(order.status);
                    return (
                      <TableRow
                        key={order.id}
                        hover
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setIsLinkedOrdersModalOpen(false);
                          navigate(`/transfer-order/completed/${order.id}${statusFilter ? `?status=${statusFilter}` : ''}`);
                        }}>
                        <TableCell>{order.id}</TableCell>
                        <TableCell>{order.referenceCode}</TableCell>
                        <TableCell>{order.sourceWarehouse?.name}</TableCell>
                        <TableCell>
                          {order.destinationWarehouse?.name}
                        </TableCell>
                        <TableCell>
                          <span
                            className={`px-2 py-1 rounded-full text-xs ${statusInfo.className}`}>
                            {statusInfo.label}
                          </span>
                        </TableCell>
                        <TableCell>{order.createdAt}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="py-4 text-center text-gray-500">
                No linked transfer orders found
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setIsLinkedOrdersModalOpen(false)}
              color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default TransferOrderPage;
